import React from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n/LanguageContext";
import { SectionTag, SectionTitle, SectionSub } from "../components/Section";
import {
  ArrowUpRight,
  Fingerprint,
  Users,
  Vote,
  FileText,
  Server,
  ShieldCheck,
  Database,
  Layers,
  Building2,
  Wifi,
  Landmark,
  Banknote,
  Mountain,
  HeartPulse,
  Tv,
  Lightbulb,
  CheckCircle2,
} from "lucide-react";
import { PDF } from "../i18n/photos";
import ClientsBand from "../components/ClientsBand";

const HERO_IMG = PDF.dataCenterEmpty;
const INFRA_IMG = PDF.serverRoomCams;
const INDUSTRIES_IMG = PDF.peopleDesks;

const STATS = [
  { value: "30+", labelKey: "hero.stat1", testId: "stat-years" },
  { value: "20+", labelKey: "hero.stat2", testId: "stat-countries" },
  { value: "8", labelKey: "hero.stat3", testId: "stat-sectors" },
  { value: "AFR", labelKey: "hero.stat4", testId: "stat-footprint" },
];

const CAPABILITIES = [
  { icon: Fingerprint, k: "cap.civil", testId: "cap-civil" },
  { icon: Users, k: "cap.census", testId: "cap-census" },
  { icon: Vote, k: "cap.elections", testId: "cap-elections" },
  { icon: FileText, k: "cap.ecm", testId: "cap-ecm" },
  { icon: Server, k: "cap.ict", testId: "cap-ict" },
  { icon: ShieldCheck, k: "cap.security", testId: "cap-security" },
  { icon: Database, k: "cap.erp", testId: "cap-erp" },
  { icon: Layers, k: "cap.simp", testId: "cap-simp", featured: true },
];

const INDUSTRIES = [
  { icon: Landmark, k: "ind.public" },
  { icon: Vote, k: "ind.elections" },
  { icon: Wifi, k: "ind.telecom" },
  { icon: Banknote, k: "ind.banking" },
  { icon: Mountain, k: "ind.mining" },
  { icon: HeartPulse, k: "ind.health" },
  { icon: Tv, k: "ind.media" },
  { icon: Building2, k: "ind.utilities" },
];

const PARTNERS = ["IBM", "ORACLE", "HP", "DELL", "EMC", "VMWARE", "KODAK", "FUJITSU", "INTERMEC", "SUN"];

const VALUES = [
  { icon: CheckCircle2, k: "val.quality" },
  { icon: CheckCircle2, k: "val.transparency" },
  { icon: CheckCircle2, k: "val.reliability" },
  { icon: CheckCircle2, k: "val.speed" },
  { icon: ShieldCheck, k: "val.security" },
  { icon: Lightbulb, k: "val.collaboration" },
];

export default function HomePage() {
  const { t } = useT();

  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden border-b border-white/10 noise-overlay" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-40" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-brand-bg" />
          <div className="absolute inset-0 grid-bg opacity-50" />
          <div className="absolute inset-0 radial-glow opacity-70" />
        </div>

        <div className="relative mx-auto flex min-h-[100vh] max-w-[1440px] flex-col justify-end px-6 pb-20 pt-40 md:px-12 lg:px-16 lg:pb-28 lg:pt-44">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-brand-red" />
              <SectionTag>{t("hero.eyebrow")}</SectionTag>
            </div>
            <h1 className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tighter text-white sm:text-5xl md:text-6xl lg:text-7xl">
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              {t("hero.sub")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-brand-red px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-redHover"
              >
                {t("hero.cta_primary")}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/simp"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-white/5"
              >
                {t("hero.cta_secondary")}
              </Link>
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-20 grid grid-cols-2 gap-px border border-white/10 bg-white/5 md:grid-cols-4">
            {STATS.map((s) => (
              <div
                key={s.value}
                data-testid={s.testId}
                className="flex flex-col gap-3 bg-brand-bg/80 p-6 backdrop-blur-md sm:p-8"
              >
                <div className="font-heading text-3xl font-medium text-white sm:text-4xl lg:text-5xl">
                  {s.value}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">
                  {t(s.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-b border-white/10 py-24 md:py-32" data-testid="capabilities">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <SectionTag>{t("section.capabilities.tag")}</SectionTag>
            <SectionTitle className="mt-5">{t("section.capabilities.title")}</SectionTitle>
            <SectionSub className="mt-5">{t("section.capabilities.sub")}</SectionSub>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-px border border-white/10 bg-white/5 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map(({ icon: Icon, k, testId, featured }) => (
              <div
                key={k}
                data-testid={testId}
                className={`group relative flex flex-col gap-6 bg-brand-bg p-8 transition-colors hover:bg-[#0E0E0E] ${
                  featured ? "sm:col-span-2 lg:col-span-2" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`flex h-11 w-11 items-center justify-center border ${featured ? "border-brand-red text-brand-red" : "border-white/15 text-white"} transition-colors group-hover:border-white/40`}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-white/30 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-red" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-heading text-xl text-white">{t(`${k}.title`)}</h3>
                  <p className="text-sm leading-relaxed text-brand-textDim">{t(`${k}.desc`)}</p>
                </div>
                {featured && (
                  <span className="absolute end-4 top-4 font-mono text-[10px] tracking-[0.25em] text-brand-red">★ FLAGSHIP</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMP showcase */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#0A0A0A] py-24 md:py-32" data-testid="simp-showcase">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionTag>{t("section.simp.tag")}</SectionTag>
              <SectionTitle className="mt-5">{t("section.simp.title")}</SectionTitle>
              <SectionSub className="mt-5">{t("section.simp.sub")}</SectionSub>
              <Link
                to="/simp"
                data-testid="simp-cta"
                className="mt-8 inline-flex items-center gap-2 bg-brand-red px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-redHover"
              >
                {t("simp.cta")} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="relative border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-textDim">
                    SIMP / core modules
                  </div>
                  <div className="flex gap-1.5">
                    <span className="h-1.5 w-1.5 bg-brand-red animate-pulse-line" />
                    <span className="h-1.5 w-1.5 bg-white/30" />
                    <span className="h-1.5 w-1.5 bg-white/30" />
                  </div>
                </div>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <li key={i} className="flex items-start gap-3 border border-white/10 bg-brand-bg p-4">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-brand-red" />
                      <span className="text-sm text-white/85">{t(`simp.bullet${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative border-b border-white/10 py-24 md:py-32" data-testid="industries">
        <div className="absolute inset-0 -z-0">
          <img src={INDUSTRIES_IMG} alt="" className="h-full w-full object-cover opacity-15" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-bg via-brand-bg/95 to-brand-bg" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <SectionTag>{t("section.industries.tag")}</SectionTag>
            <SectionTitle className="mt-5">{t("section.industries.title")}</SectionTitle>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/5 md:grid-cols-4">
            {INDUSTRIES.map(({ icon: Icon, k }) => (
              <div key={k} className="group flex flex-col gap-4 bg-brand-bg p-6 transition-colors hover:bg-[#0E0E0E]">
                <Icon className="h-6 w-6 text-white/70 transition-colors group-hover:text-brand-red" strokeWidth={1.4} />
                <span className="font-heading text-base text-white">{t(k)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS marquee */}
      <section className="border-b border-white/10 py-20" data-testid="partners">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between gap-6">
            <SectionTag>{t("section.partners.tag")}</SectionTag>
            <div className="hidden h-px flex-1 bg-white/10 md:block" />
          </div>
          <h3 className="mt-5 font-heading text-2xl tracking-tight text-white sm:text-3xl">
            {t("section.partners.title")}
          </h3>
        </div>
        <div className="mt-12 overflow-hidden">
          <div className="marquee-track gap-16 px-6">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span
                key={`${p}-${i}`}
                className="font-heading text-3xl font-medium tracking-tight text-white/30 transition-colors hover:text-white sm:text-4xl"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <ClientsBand />

      {/* VALUES */}
      <section className="border-b border-white/10 py-24 md:py-32" data-testid="values">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionTag>{t("section.values.tag")}</SectionTag>
              <SectionTitle className="mt-5">{t("section.values.title")}</SectionTitle>
              <div className="mt-8 border-l-2 border-brand-red ps-6">
                <p className="font-mono-tag">{t("about.mission.label")}</p>
                <p className="mt-3 text-base leading-relaxed text-white/85">
                  {t("about.mission.body")}
                </p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-px border border-white/10 bg-white/5 sm:grid-cols-2">
                {VALUES.map(({ icon: Icon, k }) => (
                  <div key={k} className="bg-brand-bg p-6">
                    <Icon className="h-5 w-5 text-brand-red" strokeWidth={1.5} />
                    <h4 className="mt-4 font-heading text-lg text-white">{t(`${k}.title`)}</h4>
                    <p className="mt-2 text-sm text-brand-textDim">{t(`${k}.desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INFRA banner */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden" data-testid="infra-banner">
        <img src={INFRA_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-black/70 to-black/40" />
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full max-w-[1440px] px-6 pb-16 md:px-12 lg:px-16">
            <SectionTag>Engineered in Africa · Operated worldwide</SectionTag>
            <h3 className="mt-4 max-w-3xl font-heading text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl">
              Resilient infrastructure for the next 30 years.
            </h3>
          </div>
        </div>
      </section>
    </main>
  );
}
